import {z} from "zod";
import {baseFlowFormSchema, NO_VARIANTS_MESSAGE} from "../../flow-form/schema";
import {COMBINE_MODES, LAST_DAY_OF_MONTH, RETRY_RULES, WEEKDAYS} from "./constants";
import {formatDays, formatHours, getScheduleCoverage, parseDayRange} from "./helpers";

const hoursSchema = z.array(z.number().int().min(0).max(23));

const scheduleRowSchema = z.object({
  days: z.string(),
  hours: hoursSchema,
});

const policySchema = z
  .object({
    max_day: z.number({error: "Enter the last retry day"}).int().min(1, "Stop after must be at least 1 day"),
    combine: z.enum(COMBINE_MODES),
    order: z.array(z.enum(RETRY_RULES)),
    permitted_hours: hoursSchema,
    schedule: z.array(scheduleRowSchema),
    weekday_hours: z.record(z.enum(WEEKDAYS), hoursSchema),
    skip_weekdays: z.array(z.enum(WEEKDAYS)),
    peak_day: z.array(z.union([z.number().int().min(1).max(31), z.literal(LAST_DAY_OF_MONTH)])),
    peak_day_hours: hoursSchema,
    peak_min_day: z.number({error: "Enter the peak start day"}).int().min(1, "Peak start day must be at least 1"),
  })
  .check((ctx) => {
    const {schedule, max_day} = ctx.value;
    const hasMaxDay = Number.isFinite(max_day);
    schedule.forEach((row, index) => {
      const range = parseDayRange(row.days);
      if (!range) {
        ctx.issues.push({
          code: "custom",
          path: ["schedule", index, "days"],
          message: `Schedule day "${row.days}" must be a day or a range like 4-14`,
          input: row.days,
        });
      } else if (hasMaxDay && (range.start < 1 || range.end > max_day)) {
        ctx.issues.push({
          code: "custom",
          path: ["schedule", index, "days"],
          message: `Schedule days must be within 1-${max_day}`,
          input: row.days,
        });
      }
    });
  });

const variantSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    weight: z.number(),
    policy: policySchema,
  })
  .check((ctx) => {
    const {name, policy} = ctx.value;
    const permitted = new Set(policy.permitted_hours);
    const reportUnpermitted = (hours: number[], path: (string | number)[], where: string) => {
      const unpermitted = hours.filter((hour) => !permitted.has(hour));
      if (unpermitted.length === 0) return;
      ctx.issues.push({
        code: "custom",
        path: ["policy", ...path],
        message: `${name}: ${formatHours(unpermitted)} in ${where} ${
          unpermitted.length > 1 ? "aren't permitted hours" : "isn't a permitted hour"
        }`,
        input: hours,
      });
    };
    policy.schedule.forEach((row, index) =>
      reportUnpermitted(row.hours, ["schedule", index, "hours"], `the schedule (day ${row.days})`)
    );
    WEEKDAYS.forEach((weekday) =>
      reportUnpermitted(policy.weekday_hours[weekday], ["weekday_hours", weekday], `${weekday} extra hours`)
    );
    reportUnpermitted(policy.peak_day_hours, ["peak_day_hours"], "peak hours");

    if (!Number.isFinite(policy.max_day)) return;
    const {overlapping} = getScheduleCoverage(policy.schedule, policy.max_day);
    if (overlapping.length === 0) return;
    policy.schedule.forEach((row, index) => {
      const range = parseDayRange(row.days);
      if (!range || !overlapping.some((day) => day >= range.start && day <= range.end)) return;
      ctx.issues.push({
        code: "custom",
        path: ["policy", "schedule", index, "days"],
        message: `${name}: schedule days ${formatDays(overlapping)} are covered by more than one row`,
        input: row.days,
      });
    });
  });

export const formSchema = baseFlowFormSchema.extend({
  variants: z.array(variantSchema).min(1, NO_VARIANTS_MESSAGE),
});

export type FormValues = z.infer<typeof formSchema>;
export type PolicyFormValues = FormValues["variants"][number]["policy"];
export type VariantFormValues = FormValues["variants"][number];
